import fs from 'fs/promises'
import path from 'path'
import chalk from 'chalk'

interface SyncOptions {
  schema: string
  example: string
  force: boolean
}

export async function sync(options: SyncOptions) {
  const { schema, example, force } = options

  console.log(chalk.blue('🔄 Syncing .env.example with schema...\n'))

  try {
    // Check if schema file exists
    await fs.access(schema)
  } catch {
    throw new Error(`Schema file not found: ${schema}`)
  }

  // Check if .env.example already exists
  try {
    await fs.access(example)
    if (!force) {
      console.log(chalk.yellow(`⚠️  File ${example} already exists. Use --force to overwrite.`))
      return
    }
  } catch {
    // File doesn't exist, that's fine
  }

  // Load the schema
  const { loadSchemaFile } = await import('./utils')
  const schemaModule = await loadSchemaFile(schema)
  if (!schemaModule.env) {
    throw new Error(`No 'env' export found in ${schema}`)
  }

  const envSchema = schemaModule.env
  const exampleContent: string[] = []
  
  // Add header
  exampleContent.push('# Environment Variables')
  exampleContent.push('# Copy this file to .env and fill in your values')
  exampleContent.push('')

  // Process client variables
  if (envSchema.client && Object.keys(envSchema.client).length > 0) {
    exampleContent.push('# Client-side variables (available in browser)')
    Object.entries(envSchema.client).forEach(([key, schema]) => {
      const defaultValue = (schema as any)._def.defaultValue
      const comment = getSchemaComment(schema as any)
      
      if (defaultValue !== undefined) {
        exampleContent.push(`${key}=${defaultValue}${comment}`)
      } else {
        exampleContent.push(`${key}=${comment}`)
      }
    })
    exampleContent.push('')
  }

  // Process server variables
  if (envSchema.server && Object.keys(envSchema.server).length > 0) {
    exampleContent.push('# Server-side variables (not available in browser)')
    Object.entries(envSchema.server).forEach(([key, schema]) => {
      const defaultValue = (schema as any)._def.defaultValue
      const comment = getSchemaComment(schema as any)
      
      if (defaultValue !== undefined) {
        exampleContent.push(`${key}=${defaultValue}${comment}`)
      } else {
        exampleContent.push(`${key}=${comment}`)
      }
    })
    exampleContent.push('')
  }

  // Write the file
  await fs.writeFile(example, exampleContent.join('\n'), 'utf-8')

  console.log(chalk.green(`✅ Created ${example}`))
  console.log(chalk.blue(`\n📝 Next steps:`))
  console.log(chalk.gray(`1. Copy ${example} to .env`))
  console.log(chalk.gray(`2. Fill in your actual values in .env`))
  console.log(chalk.gray(`3. Run 'envfortress validate' to check your setup`))
}

function getSchemaComment(schema: any): string {
  const type = schema._def.typeName
  
  switch (type) {
    case 'ZodString':
      if (schema._def.checks?.some((check: any) => check.kind === 'url')) {
        return ' # URL (e.g., https://api.example.com)'
      }
      return ' # string'
      
    case 'ZodNumber':
      return ' # number'
      
    case 'ZodBoolean':
      return ' # boolean (true/false)'
      
    case 'ZodEnum':
      const values = schema._def.values
      return ` # enum (${values.join(' | ')})`
      
    case 'ZodUnion':
      return ' # union type'
      
    case 'ZodArray':
      return ' # array (comma-separated)'
      
    case 'ZodObject':
      return ' # JSON object'
      
    default:
      return ''
  }
} 